import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const differentiators = [
  {
    icon: 'ri-time-line',
    title: '60+ Years Experience',
    description:
      'Our leadership brings more than six decades of combined expertise across engineering, construction, and strategic development, ensuring every project benefits from seasoned insight.',
  },
  {
    icon: 'ri-map-2-line',
    title: 'Nationwide Reach',
    description:
      'With active operations across 20+ states and growing, Holston Development Group delivers consistent quality and responsiveness from coast to coast.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Multi-Discipline Expertise',
    description:
      'Unlike single-focus firms, we operate across multiple specialized disciplines under one group, providing cohesive solutions for the most complex projects.',
  },
  {
    icon: 'ri-user-heart-line',
    title: 'Client-Focused Solutions',
    description:
      'We build long-term partnerships by listening first. Every project is tailored to your specific goals, timeline, and operational needs, not a template.',
  },
];

export default function DifferentiatorsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const children = sectionRef.current?.querySelectorAll('.fade-in-child');
            children?.forEach((child, i) => {
              const el = child as HTMLElement;
              setTimeout(() => {
                el.style.transition = 'opacity 0.7s ease, transform 0.7s ease';
                el.style.opacity = '1';
                el.style.transform = 'translateY(0)';
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p
            className="fade-in-child text-[#0050B2] text-sm font-semibold uppercase tracking-widest mb-3"
            style={{ opacity: 0, transform: 'translateY(20px)' }}
          >
            Why Holston
          </p>
          <h2
            className="fade-in-child text-3xl lg:text-4xl font-bold text-[#001F4E]"
            style={{ opacity: 0, transform: 'translateY(20px)' }}
          >
            The Holston Difference
          </h2>
          <p
            className="fade-in-child text-gray-500 mt-4 text-base"
            style={{ opacity: 0, transform: 'translateY(20px)' }}
          >
            What sets us apart isn&apos;t just our capabilities. It&apos;s how we apply them.
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {differentiators.map((item, i) => (
            <div
              key={i}
              className="fade-in-child p-6 rounded-lg border border-[#E8EFF6] hover:border-[#0050B2]/30 hover:bg-[#E8EFF6]/40 transition-all duration-300"
              style={{ opacity: 0, transform: 'translateY(20px)' }}
            >
              <div className="w-11 h-11 flex items-center justify-center rounded-md bg-[#E8EFF6] mb-4">
                <i className={`${item.icon} text-xl text-[#0050B2]`} />
              </div>
              <h3 className="text-[#001F4E] font-bold text-base mb-2">{item.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>

        {/* CTA Band */}
        <div
          className="fade-in-child rounded-lg bg-[#001F4E] px-8 py-10 lg:px-14 lg:py-12 flex flex-col lg:flex-row items-center justify-between gap-6"
          style={{ opacity: 0, transform: 'translateY(20px)' }}
        >
          <div>
            <h3 className="text-white text-xl lg:text-2xl font-bold mb-2">
              Ready to work with Holston Development Group?
            </h3>
            <p className="text-white/70 text-sm">
              Let&apos;s discuss how we can deliver strategic solutions for your next project.
            </p>
          </div>
          <Link
            to="/contact"
            className="shrink-0 px-7 py-3 rounded-md bg-[#0050B2] text-white font-semibold text-sm hover:bg-[#00A3E0] transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            Contact Us Today
          </Link>
        </div>
      </div>
    </section>
  );
}
